// ─── PLATFORM SETTINGS ────────────────────────────────────────────────────────
// Behaviour settings only — squawk box and keyword alerts. Account, plan, the
// Anthropic BYOK key, billing and sign-out all stay in SettingsModal
// (src/config.jsx), which the person icon in the header opens. Two icons, two
// concerns: 👤 is who you are, ⚙ is how the terminal behaves.
//
// Visual shell deliberately mirrors SettingsModal — same fixed overlay, same
// 440px card, same Section() rhythm, same X close — so the two panels read as
// one system.

import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { C, s } from "./core";
import {
  SQUAWK_ENABLED_KEY,
  SQUAWK_VOLUME_KEY,
  SQUAWK_VOICE_KEY,
  CHYRON_KEYWORDS_KEY,
  VOICE_RATE,
  VOICE_PITCH,
  loadSquawkEnabled,
  loadSquawkVolume,
  loadSquawkVoice,
  publishSettingsChange,
  SQUAWK_KOKORO_VOICE_KEY,
  KOKORO_VOICES,
  loadKokoroVoice,
  kokoroSupported,
} from "./components/Chyron";

// ⚠ Local copy of config.jsx's Section — that one is module-private and this
// file is not allowed to touch config.jsx. Kept byte-identical in styling so the
// two panels cannot drift apart visually.
function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 11, color: C.amber, letterSpacing: "0.12em", fontWeight: 700, marginBottom: 8 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

// The keyword field is edited as a raw comma-separated string and only parsed on
// save, so a half-typed "Fed, NV" never truncates to a live keyword mid-keystroke.
function loadKeywordString() {
  try {
    const raw = localStorage.getItem(CHYRON_KEYWORDS_KEY);
    return typeof raw === "string" ? raw : "";
  } catch { return ""; }
}

// ⚠ en-US first, then the other English variants alphabetically. Anything
// non-English is dropped — the headlines are English, and a Spanish voice
// reading them is not a preference, it is a mistake waiting to be made.
function sortEnglishVoices(all) {
  return all
    .filter((v) => v && typeof v.lang === "string" && v.lang.toLowerCase().startsWith("en"))
    .sort((a, b) => {
      const aUS = a.lang.toLowerCase() === "en-us" ? 0 : 1;
      const bUS = b.lang.toLowerCase() === "en-us" ? 0 : 1;
      if (aUS !== bUS) return aUS - bUS;
      return (a.name || "").localeCompare(b.name || "");
    });
}

// Preferred default when the user has never chosen: Google US English if the
// browser ships it, else the first en-US, else the first English voice at all.
function defaultVoiceName(list) {
  if (!list.length) return "";
  const google = list.find((v) => /google\s+us\s+english/i.test(v.name || ""));
  if (google) return google.name;
  const us = list.find((v) => v.lang.toLowerCase() === "en-us");
  return (us || list[0]).name;
}

export function SettingsPanel({ onClose }) {
  const [squawkEnabled, setSquawkEnabled] = useState(loadSquawkEnabled);
  const [volume,        setVolume]        = useState(loadSquawkVolume);
  const [keywordText,   setKeywordText]   = useState(loadKeywordString);
  const [savedMsg,      setSavedMsg]      = useState("");
  const [voices,        setVoices]        = useState([]);
  const [voicesReady,   setVoicesReady]   = useState(false);
  const [voiceName,     setVoiceName]     = useState(loadSquawkVoice);
  const [voiceMissing,  setVoiceMissing]  = useState("");
  const [previewing,    setPreviewing]    = useState(false);
  const [kokoroVoice,   setKokoroVoice]   = useState(loadKokoroVoice);
  // ⚠ Read ONCE into state rather than called during render. navigator.gpu is
  // stable for the life of the page, and a render-time call would re-run on
  // every keystroke in the keyword box for no reason.
  const [neuralOk]                        = useState(kokoroSupported);

  const savedTimer   = useRef(null);
  const previewRef   = useRef(null);   // retains the utterance against GC
  const previewTimer = useRef(null);
  useEffect(() => () => {
    clearTimeout(savedTimer.current);
    clearTimeout(previewTimer.current);
  }, []);

  // ── Voice list ──
  // ⚠⚠ getVoices() RETURNS [] ON THE FIRST CALL IN CHROME and fills in
  // asynchronously, so the list must be built from the voiceschanged event as
  // well as read directly. Reading once on mount is the classic way to ship an
  // empty dropdown that works fine on the developer's warm browser.
  // ⚠ The settle timer exists for the opposite case: a device with no TTS voices
  // at all never fires voiceschanged, and without it the UI would sit on
  // "Loading voices…" forever with no way to tell that apart from a slow load.
  useEffect(() => {
    const synth = window.speechSynthesis;
    if (!synth) { setVoicesReady(true); return undefined; }
    let done = false;
    const collect = () => {
      if (done) return;
      const list = sortEnglishVoices(synth.getVoices() || []);
      if (!list.length) return;
      setVoices(list);
      setVoicesReady(true);
      done = true;
      setVoiceName((prev) => {
        if (prev && list.some((v) => v.name === prev)) return prev;
        const fallback = defaultVoiceName(list);
        // ⚠ The fallback is PERSISTED, not just displayed. If the dropdown showed
        // one voice while storage held another (or nothing), the ticker would
        // speak in a voice the settings screen never named — which reads as a
        // bug in the voice picker rather than as a missing preference.
        if (prev) setVoiceMissing(prev);
        try { localStorage.setItem(SQUAWK_VOICE_KEY, fallback); } catch { /* ignore */ }
        publishSettingsChange();
        return fallback;
      });
    };
    collect();
    synth.addEventListener?.("voiceschanged", collect);
    const settle = setTimeout(() => { if (!done) setVoicesReady(true); }, 2000);
    return () => {
      done = true;
      clearTimeout(settle);
      synth.removeEventListener?.("voiceschanged", collect);
    };
  }, []);

  // ⚠ Separate key AND separate handler from changeVoice. The two engines keep
  // independent preferences — see the note on SQUAWK_KOKORO_VOICE_KEY.
  const changeKokoroVoice = (id) => {
    setKokoroVoice(id);
    try { localStorage.setItem(SQUAWK_KOKORO_VOICE_KEY, id); } catch { /* ignore */ }
    publishSettingsChange();
  };

  const changeVoice = (name) => {
    setVoiceName(name);
    setVoiceMissing("");
    try { localStorage.setItem(SQUAWK_VOICE_KEY, name); } catch { /* ignore */ }
    publishSettingsChange();
  };

  // ⚠ PREVIEW does NOT cancel the ticker's queue. speechSynthesis is a single
  // shared engine, so cancel() here would silently wipe whatever the squawk box
  // was reading and stall its pump. The preview simply queues behind it.
  const preview = () => {
    const synth = window.speechSynthesis;
    if (!synth || previewing) return;
    try {
      const u = new SpeechSynthesisUtterance("Squawk box. Market update.");
      const match = (synth.getVoices() || []).find((v) => v && v.name === voiceName);
      if (match) u.voice = match;
      u.rate   = VOICE_RATE;    // identical to the ticker's, or this proves nothing
      u.pitch  = VOICE_PITCH;
      u.volume = Math.min(1, Math.max(0, volume / 100));
      previewRef.current = u;   // Chrome GCs a mid-flight utterance
      const done = () => {
        clearTimeout(previewTimer.current);
        setPreviewing(false);
      };
      u.onend = done;
      u.onerror = done;
      setPreviewing(true);
      // Same latch problem as the ticker's watchdog: if onend never fires the
      // button would stay disabled for the life of the panel.
      previewTimer.current = setTimeout(done, 8000);
      synth.speak(u);
    } catch {
      setPreviewing(false);
    }
  };

  const flashSaved = (msg) => {
    setSavedMsg(msg);
    clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSavedMsg(""), 2000);
  };

  // Toggle and volume write on change (there is nothing to "commit" — the
  // control IS the value). Keywords write on SAVE. Every write publishes the
  // change event so a mounted Chyron picks it up without a reload.
  const toggleSquawk = () => {
    const next = !squawkEnabled;
    setSquawkEnabled(next);
    try { localStorage.setItem(SQUAWK_ENABLED_KEY, next ? "true" : "false"); } catch { /* ignore */ }
    publishSettingsChange();
  };

  const changeVolume = (raw) => {
    const n = Math.min(100, Math.max(0, Math.round(Number(raw) || 0)));
    setVolume(n);
    try { localStorage.setItem(SQUAWK_VOLUME_KEY, String(n)); } catch { /* ignore */ }
    publishSettingsChange();
  };

  const saveKeywords = () => {
    // Normalize on the way in so the stored value is what the matcher expects:
    // trimmed, empties dropped, comma-joined.
    const cleaned = keywordText.split(",").map((k) => k.trim()).filter(Boolean);
    try {
      if (cleaned.length) localStorage.setItem(CHYRON_KEYWORDS_KEY, cleaned.join(", "));
      // ⚠ Clearing the field REMOVES the key rather than storing "" — an empty
      // string and "never set" must not be two different states to any reader.
      else localStorage.removeItem(CHYRON_KEYWORDS_KEY);
    } catch { /* ignore */ }
    setKeywordText(cleaned.join(", "));
    publishSettingsChange();
    flashSaved(cleaned.length
      ? `Saved · ${cleaned.length} keyword${cleaned.length !== 1 ? "s" : ""}`
      : "Cleared");
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.88)", zIndex: 200,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        width: 440, maxWidth: "94vw", background: C.panel, border: `1px solid ${C.border}`,
        borderRadius: 4, padding: 20, maxHeight: "90vh", overflowY: "auto",
      }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <span style={{ color: C.amber, fontSize: 14, fontWeight: 700, letterSpacing: "0.15em" }}>
            SETTINGS
          </span>
          <button style={{ ...s.btn(), padding: "2px 6px" }} onClick={onClose} aria-label="Close settings">
            <X size={11} />
          </button>
        </div>

        {/* ── SQUAWK BOX ── */}
        <Section title="SQUAWK BOX">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <span style={{ fontSize: 12, color: C.textMid }}>Spoken headline readout</span>
            <button
              style={{ ...s.btn(squawkEnabled ? "teal" : "default"), minWidth: 64 }}
              onClick={toggleSquawk}
              aria-pressed={squawkEnabled}
            >
              {squawkEnabled ? "🔊 ON" : "🔇 OFF"}
            </button>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 12, color: C.textMid, whiteSpace: "nowrap" }}>Volume</span>
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              value={volume}
              onChange={(e) => changeVolume(e.target.value)}
              aria-label="Squawk volume"
              style={{ flex: 1, accentColor: C.amber, cursor: "pointer" }}
            />
            <span style={{
              fontSize: 12, color: C.text, fontWeight: 700,
              minWidth: 32, textAlign: "right",
            }}>
              {volume}
            </span>
          </div>
          <div style={{ fontSize: 11, color: C.textDim, marginTop: 6, lineHeight: 1.5 }}>
            Applies to both the alert chime and the spoken readout.
          </div>

          {/* ── Neural voice (Kokoro) ── */}
          {/* ⚠ Shown ONLY where it can actually run. WebGPU is absent on iOS
              Safari and partial on Android Chrome, so on most phones this
              section is hidden entirely and the system-voice picker below is
              the whole story — which is the honest presentation, rather than
              offering a choice that silently does nothing. */}
          {neuralOk && (
            <>
              <div style={{ fontSize: 12, color: C.textMid, marginTop: 12, marginBottom: 4 }}>
                NEURAL VOICE
              </div>
              <select
                style={{ ...s.inp, width: "100%", cursor: "pointer" }}
                value={kokoroVoice}
                onChange={(e) => changeKokoroVoice(e.target.value)}
                aria-label="Neural squawk voice"
              >
                {KOKORO_VOICES.map((v) => (
                  <option key={v.id} value={v.id}>{v.label}</option>
                ))}
              </select>
              <div style={{ fontSize: 11, color: C.textDim, marginTop: 6, lineHeight: 1.5 }}>
                Runs locally on your GPU. The voice model downloads once (~80MB)
                on the first squawk and is cached afterwards; the system voice
                below is used until it is ready, and any time it is unavailable.
              </div>
            </>
          )}

          {/* ── Voice ── */}
          <div style={{ fontSize: 12, color: C.textMid, marginTop: 12, marginBottom: 4 }}>
            {neuralOk ? "SYSTEM VOICE (FALLBACK)" : "VOICE"}
          </div>
          {!voicesReady ? (
            <div style={{ fontSize: 12, color: C.textDim }}>Loading voices…</div>
          ) : voices.length === 0 ? (
            <div style={{ fontSize: 11, color: C.textDim, lineHeight: 1.5 }}>
              No speech voices are installed on this device — the squawk box will
              stay silent.
            </div>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <select
                  style={{ ...s.inp, flex: 1, minWidth: 0, cursor: "pointer" }}
                  value={voiceName}
                  onChange={(e) => changeVoice(e.target.value)}
                  aria-label="Squawk voice"
                >
                  {voices.map((v) => (
                    <option key={v.name} value={v.name}>
                      {v.name} ({v.lang})
                    </option>
                  ))}
                </select>
                <button
                  style={{ ...s.btn("teal"), whiteSpace: "nowrap" }}
                  onClick={preview}
                  disabled={previewing}
                  aria-label="Preview squawk voice"
                >
                  {previewing ? "▶ PLAYING…" : "▶ PREVIEW"}
                </button>
              </div>
              {voiceMissing && (
                <div style={{ fontSize: 11, color: "#FFB300", marginTop: 6, lineHeight: 1.5 }}>
                  Saved voice “{voiceMissing}” isn’t available on this device — switched to{" "}
                  {voiceName}.
                </div>
              )}
              <div style={{ fontSize: 11, color: C.textDim, marginTop: 6, lineHeight: 1.5 }}>
                English voices only. The list comes from your browser, so it differs
                by device.
              </div>
            </>
          )}
        </Section>

        {/* ── KEYWORD ALERTS ── */}
        <Section title="KEYWORD ALERTS">
          <div style={{ fontSize: 12, color: C.textMid, marginBottom: 4 }}>Alert Keywords</div>
          <input
            style={{ ...s.inp, marginBottom: 6 }}
            type="text"
            placeholder="Fed, NVDA, rate cut, Powell"
            value={keywordText}
            onChange={(e) => setKeywordText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") saveKeywords(); }}
            spellCheck={false}
            autoComplete="off"
          />
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button style={s.btn("teal")} onClick={saveKeywords}>SAVE</button>
            {savedMsg && (
              <span style={{ fontSize: 11, color: C.green, fontWeight: 700 }}>{savedMsg}</span>
            )}
          </div>
          <div style={{ fontSize: 11, color: C.textDim, marginTop: 6, lineHeight: 1.5 }}>
            Chime and highlight when these words appear in headlines.
          </div>
        </Section>

        {/* ⚠ Browser policy, stated here so a silent first load does not read as
            a broken feature. There is no workaround — see Chyron.jsx. */}
        <div style={{ fontSize: 11, color: C.textDim, lineHeight: 1.5, borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
          Audio stays silent until you have clicked somewhere on the page — browsers
          block sound until a page has been interacted with.
        </div>
      </div>
    </div>
  );
}

export default SettingsPanel;
